import {useState, useContext, useEffect} from 'react';
import styles from '../Applicants/css/display-applicant.module.css';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {AiOutlineSearch as SearchIcon} from 'react-icons/ai';
import {IoReload as ReloadIcon} from 'react-icons/io5';
import apiCall from '../../../utils/apiCall';
import { CircularProgress, styled } from '@mui/material';
import { AxiosError } from 'axios';
import TrainingInvitesTable from './Table';
import { ExNyRoute,PrRoute, ExRoute, AcRoute, MiRoute,  CoRoute } from '../../../utils/allRouteId';
import BatchInviteModal from './Modal/BatchInviteModal';
import { BatchInvites } from './Modal';
import { useSelector } from 'react-redux';
import {RootState} from '../../../redux/store';
import { NotificationContext } from '../../../providers/notification'; 
import {RouteResponse} from '../../../types/index';
import {FetchAllRoutes, reOrderFetchedData} from '../../../hooks/FetchData'
import { TopDisplay,Button,UploadButton,  FilterDropDown,DateFilter, } from '../../../ui-components';
import { handleDownloadTraining } from '../../../utils/downloadUtils';
import apiCallFormData from '../../../utils/apiFormDataCall';





interface TrainingInvites {
  ApplicantId: string;
  ApplicantName: string;
  ApplicantEmail: string;
  ApplicantPhone: string;
}

interface PostTrainingInvites {
  ApplicantId: string[];
  RouteId?: number;
  Activity: string;
}

const option1:any= [
  {val:ExNyRoute, name:"Examination / NYSC",path:``},
  {val:PrRoute, name:"Practitioner",path:`practitioner`},
  {val:ExRoute, name:"Executive",path:`executive`},
  {val:AcRoute, name:"Academic",path:`academic`},
  {val:MiRoute, name:"Military",path:`military`},
  {val:CoRoute, name:"Corporate",path:`corporate`}
]
interface DisplayTrainingInvitesProps {
  setRoute: React.Dispatch<
    React.SetStateAction<{
      name: string;
      val: number;
    }>
  >;
  route?: {
    name: string;
    val: number;
  };
  showBatchInviteModal: boolean;
  handleCloseBatchInvite: () => void;
}

const DisplayTrainingInvites = ({
  setRoute,
  route,
  handleCloseBatchInvite,
  showBatchInviteModal,
}: DisplayTrainingInvitesProps) => {
  const [searchData, setSearchData] = useState("");
  const [routeNameInt, setRouteNameInt] = useState("");
  const batchInviteData = useSelector((state: RootState) => state?.batchInvite?.data);
  const { showNotification } = useContext(NotificationContext);
  const queryClient = useQueryClient();

  const { isLoading, data } = FetchAllRoutes();
  const reOrderedAllRoutes = data ? reOrderFetchedData(data?.Data) : [];
  const allRoutes = isLoading
    ? []
    : reOrderedAllRoutes.map((item) => ({ name: item?.RouteName, val: item?.RouteId }));

  // fetch invites
  const getTrainingInvites = async (): Promise<TrainingInvites[]> =>
    (await apiCall().post(`/Activity/GetApplicantPendingTraining?routeId=${route?.val}&activity=Training`))
      ?.data?.Data;

  const getTrainingInvitesMutation = useMutation(["get-Training-Invites"], getTrainingInvites);
  const getTrainingInvitesError = getTrainingInvitesMutation?.error as AxiosError;

  // batch invite
  const postBatchInvite = async (data: PostTrainingInvites): Promise<any> => {
    return await apiCall().post("/Activity/BatchInviteTraining", data);
  };

  const batchInviteMutation = useMutation(["post-Training-Invites"], postBatchInvite, { retry: 0 });

  const handleBatchInviteSubmit = async (e: any) => {
    e.preventDefault();

    const BatchInvitePayloadList = batchInviteData.map((item) => item.ApplicantId);

    const BatchInvitePayload = {
      ApplicantId: BatchInvitePayloadList,
      RouteId: route?.val,
      Activity: "Training",
    };

    try {
      if (BatchInvitePayloadList.length < 1) throw "Select applicants to invite";

      await batchInviteMutation.mutateAsync(BatchInvitePayload, {
        onSuccess: () => {
          showNotification({
            message: "Batch Invite Successful",
            type: "success",
          });
          queryClient.invalidateQueries(["get-Training-Invites"]);
        },
      });
    } catch (error: any) {
      showNotification({
        message: error?.response?.data?.Message || error.message || error,
        type: "error",
      });
    }
  };

  useEffect(() => {
    getTrainingInvitesMutation.mutateAsync();
  }, [route]);

  const handleRouteSwitch = (route: any) => {
    setRoute(route);
    setRouteNameInt(route.name);
  };

  const TrainingInvites = getTrainingInvitesMutation?.data || [];
  const filteredData = TrainingInvites?.filter((x) =>
    Object.values(x).join(" ").toLowerCase().includes(searchData.toLowerCase())
  );

  // ⬇️ handle training upload directly here
  const [uploading, setUploading] = useState(false);

const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
  const file = e.target.files?.[0];
  if (!file) return;

  const body = new FormData();
  body.append("ExcelFile", file);

  setUploading(true);
  try {
    const response = await apiCallFormData().post(
      "/Applicant/UploadOfflineApplicantTraining",
      body,
      {
        responseType: "json", // expect JSON first
      }
    );

    showNotification({
      message:
        response?.data?.Message ||
        "Training file uploaded successfully",
      type: "success",
    });
  } catch (error: any) {
    const blob = error?.response?.data;

    // case 1: API returned JSON error message
    if (error?.response?.data?.Message) {
      showNotification({
        message: error.response.data.Message,
        type: "error",
      });
    }
    // case 2: API returned blob (like error report)
    else if (blob instanceof Blob) {
      // Try reading the blob as text first
      const text = await blob.text().catch(() => null);

      if (text) {
        try {
          const parsed = JSON.parse(text);
          showNotification({
            message: parsed?.Message || "Upload failed",
            type: "error",
          });
        } catch {
          // Not JSON → treat as file download
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "Payment_UploadErrorReport.xlsx");
          document.body.appendChild(link);
          link.click();
          link.remove();

          showNotification({
            message:
              "Induction upload failed — error report downloaded",
            type: "error",
          });
        }
      }
    } else {
      // fallback
      showNotification({
        message: error?.message || "File not uploaded",
        type: "error",
      });
    }
  } finally {
    setUploading(false);
    e.target.value = "";
  }
};


  return (
    <>
      <TopWrapper>
        <label className={styles.searchForm}>
          <span className={styles.sI}>
            <SearchIcon />
          </span>
          <input
            value={searchData}
            onChange={(e) => setSearchData(e.target.value)}
            type="text"
            placeholder="Search"
          />
          <button className={styles.rBtn} type="submit">
            <ReloadIcon />
          </button>
        </label>

        <FilterDropDown
          onChange={handleRouteSwitch}
          options={allRoutes}
          text={routeNameInt ? `${routeNameInt}` : "Membership Route"}
          bg="#11643C"
          color="#fff"
        />

        <UploadButton
          onChange={handleUpload}
          label={!uploading ? "Upload OffLine Training Payment" : "Uploading..."}
        />

        <Button
          onClick={handleDownloadTraining}
          children="Download Offline Payment Template"
          border="#E2E4E9"
          bg="#11643C"
          color="#fff"
        />
      </TopWrapper>

      {getTrainingInvitesMutation?.isLoading ? (
        <CircularProgress sx={{ fontSize: "1rem", margin: "2rem auto", display: "block" }} />
      ) : getTrainingInvitesMutation?.isError ? (
        <StyledError>{getTrainingInvitesError?.message}</StyledError>
      ) : filteredData && filteredData?.length > 0 ? (
        <TrainingInvitesTable
          data={searchData.length > 0 ? filteredData : TrainingInvites}
          routeId={route?.val}
        />
      ) : (
        <div>No Result</div>
      )}

      <BatchInviteModal
        open={showBatchInviteModal}
        cancel={handleCloseBatchInvite}
        header={"Batch Invites"}
        maxWidth={"1300px"}
        content={
          getTrainingInvitesMutation?.isLoading ? (
            <CircularProgress sx={{ fontSize: "1rem", margin: "2rem auto", display: "block" }} />
          ) : getTrainingInvitesMutation?.isError ? (
            <StyledError>{getTrainingInvitesError?.message}</StyledError>
          ) : (
            <BatchInvites
              isLoading={batchInviteMutation.isLoading}
              handleBatchInviteSubmit={handleBatchInviteSubmit}
              data={searchData.length > 0 ? filteredData : TrainingInvites}
            />
          )
        }
      />
    </>
  );
};

export default DisplayTrainingInvites;

const StyledError = styled('p')`
  color: red;
  font-size: 1.4rem;
  display: flex;
`

const TopWrapper = styled('div')`
  display:flex;
  gap:10px;
  align-items: center;
  margin: 10px 0px 48px 0px;
`
